import { formatCurrency, formatRupiah, parseRupiah } from '../../../utils/format';
import { useLanguage } from '../../../features/language/useLanguage';

type DiscountType = 'percent' | 'amount';

interface SalesOrderTotalsProps {
  subtotalAmount: number;
  discountType: DiscountType;
  discountValue: number;
  discountAmount: number;
  shippingAmount: number;
  totalAmount: number;
  balanceDue: number;
  currencySymbol: string;
  areFieldsDisabled: boolean;
  onShippingChange: (value: number) => void;
  onDiscountTypeChange: (type: DiscountType) => void;
  onDiscountValueChange: (value: number) => void;
}

const inputClass =
  'w-36 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-right text-sm font-semibold text-gray-900 tabular-nums shadow-sm transition-colors focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-400';

/** Subtotal / discount / shipping / total / balance-due summary for the sales order form. */
export function SalesOrderTotals({
  subtotalAmount,
  discountType,
  discountValue,
  discountAmount,
  shippingAmount,
  totalAmount,
  balanceDue,
  currencySymbol,
  areFieldsDisabled,
  onShippingChange,
  onDiscountTypeChange,
  onDiscountValueChange,
}: SalesOrderTotalsProps) {
  const { t } = useLanguage();

  // Switching mode keeps the *effective* discount intact: 20% becomes its Rp
  // equivalent (and vice versa) against the current subtotal, instead of
  // reinterpreting the raw "20" as Rp 20.
  const handleModeChange = (newType: DiscountType) => {
    if (newType === discountType) {
      return;
    }
    let converted = discountValue;
    if (subtotalAmount > 0 && discountValue > 0) {
      if (newType === 'amount') {
        // percent -> Rp: round to whole rupiah
        converted = Math.round((subtotalAmount * Math.min(discountValue, 100)) / 100);
      } else {
        // Rp -> percent: cap at 100%, keep 2 decimals
        converted = Math.min(Math.round((discountValue / subtotalAmount) * 10000) / 100, 100);
      }
    }
    onDiscountTypeChange(newType);
    onDiscountValueChange(converted);
  };

  // Surface the effective deduction only when it isn't already obvious from the
  // input: always in percent mode, and in Rp mode only when the value was
  // capped down to the subtotal.
  const showDiscountNote =
    discountAmount > 0 && (discountType === 'percent' || discountAmount !== discountValue);

  const toggleBtn = (type: DiscountType, label: string) => (
    <button
      type="button"
      data-test-id={`so-discount-mode-${type}`}
      aria-pressed={discountType === type}
      onClick={() => handleModeChange(type)}
      disabled={areFieldsDisabled}
      className={`min-w-[2rem] rounded px-2 py-1 text-xs font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 disabled:opacity-50 ${
        discountType === type
          ? 'bg-white text-gray-900 shadow-sm'
          : 'text-gray-500 hover:text-gray-700'
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="md:ml-auto md:max-w-md">
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="divide-y divide-gray-100">
          {/* Subtotal */}
          <div className="flex items-center justify-between px-4 py-3">
            <span className="text-sm text-gray-500">{t('salesOrder.items.subtotal')}</span>
            <span data-test-id="so-subtotal" className="text-sm font-semibold text-gray-900 tabular-nums">
              {formatCurrency(subtotalAmount, currencySymbol)}
            </span>
          </div>

          {/* Discount */}
          <div className="px-4 py-3">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <label htmlFor="discountValue" className="text-sm text-gray-500">
                  {t('salesOrder.items.discount')}
                </label>
                <div className="inline-flex rounded-md bg-gray-100 p-0.5" role="group" aria-label="Discount mode">
                  {toggleBtn('amount', currencySymbol)}
                  {toggleBtn('percent', '%')}
                </div>
              </div>

              {discountType === 'percent' ? (
                <div className="relative w-36">
                  <input
                    type="number"
                    min={0}
                    max={100}
                    step="any"
                    id="discountValue"
                    data-test-id="so-discount-input"
                    value={discountValue === 0 ? '' : discountValue}
                    onChange={(e) => onDiscountValueChange(Math.max(0, Number(e.target.value) || 0))}
                    disabled={areFieldsDisabled}
                    placeholder="0"
                    className={`${inputClass} pr-6`}
                  />
                  <span className="pointer-events-none absolute inset-y-0 right-2.5 flex items-center text-sm text-gray-400">
                    %
                  </span>
                </div>
              ) : (
                <input
                  type="text"
                  id="discountValue"
                  data-test-id="so-discount-input"
                  value={formatRupiah(discountValue)}
                  onChange={(e) => onDiscountValueChange(parseRupiah(e.target.value))}
                  disabled={areFieldsDisabled}
                  className={inputClass}
                />
              )}
            </div>
            {showDiscountNote && (
              <p
                data-test-id="so-discount-applied"
                className="mt-1.5 text-right text-xs text-gray-400 tabular-nums"
              >
                −{formatCurrency(discountAmount, currencySymbol)}
              </p>
            )}
          </div>

          {/* Shipping */}
          <div className="flex items-center justify-between px-4 py-3">
            <label htmlFor="shippingAmount" className="text-sm text-gray-500">
              {t('salesOrder.items.shipping')}
            </label>
            <input
              type="text"
              value={formatRupiah(shippingAmount)}
              id="shippingAmount"
              data-test-id="so-shipping-cost"
              onChange={(e) => onShippingChange(parseRupiah(e.target.value))}
              disabled={areFieldsDisabled}
              className={inputClass}
            />
          </div>

          {/* Total */}
          <div className="flex items-center justify-between px-4 py-3">
            <span className="text-sm font-medium text-gray-600">{t('reports.details.total')}</span>
            <span data-test-id="so-total" className="text-sm font-bold text-gray-900 tabular-nums">
              {formatCurrency(totalAmount, currencySymbol)}
            </span>
          </div>
        </div>

        {/* Balance due — the figure that matters, given a warm accent band. */}
        <div className="flex items-center justify-between border-t border-primary-100 bg-primary-50 px-4 py-3.5">
          <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">
            {t('salesOrder.items.balanceDue')}
          </span>
          <span data-test-id="so-balance-due-footer" className="text-lg font-bold text-primary-700 tabular-nums">
            {formatCurrency(balanceDue, currencySymbol)}
          </span>
        </div>
      </div>
    </div>
  );
}
